import React, { useState } from 'react';
import PageShell from '../../components/PageShell';
import { GradientButton, LetterBadge } from '../../components/common/Primitives';
import LicenceFeeHighlight from '../../components/common/LicenceFeeHighlight';
import { PARTNERSHIP_REQUEST } from '../../lib/mockPartners';
import { submitForm } from '../../lib/api';
import { Check, CheckCircle2, ArrowRight } from 'lucide-react';

const inputCls = 'w-full rounded-lg border border-canvas-200 bg-white px-4 py-2.5 text-[14px] text-navy placeholder:text-ink-500/60 focus:outline-none focus:ring-2 focus:ring-purple/40';
const labelCls = 'text-[13px] font-semibold text-ink-700';

export default function PartnershipRequest() {
  const d = PARTNERSHIP_REQUEST;
  const [form, setForm] = useState({
    org: '', website: '', contact: '', role: '', email: '', phone: '',
    orgType: '', geography: '', institutions: '', learners: '', timeline: '',
    interests: [],
    objective: '', contribution: '', questions: '',
    agreements: [false, false],
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const toggleArr = (k, val) => setForm(p => ({ ...p, [k]: p[k].includes(val) ? p[k].filter(x => x !== val) : [...p[k], val] }));
  const toggleAgree = (i) => setForm(p => ({ ...p, agreements: p.agreements.map((c, ci) => ci === i ? !c : c) }));
  const allAgreed = form.agreements.every(Boolean);
  const hasInterest = form.interests.length > 0;
  const canSubmit = allAgreed && hasInterest && !submitting;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true); setSubmitError(null);
    try {
      await submitForm({
        formType: 'partnership-request',
        fields: form,
        contactEmail: form.email,
        contactName: form.contact,
      });
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setSubmitError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <PageShell>
        <section className="px-6 py-24" data-testid="partnership-request-confirmation">
          <div className="mx-auto max-w-[720px]">
            <div className="rounded-3xl border border-mint/30 bg-gradient-to-br from-mint-50 via-cyan-50 to-purple-50 p-10 shadow-soft text-center">
              <div className="text-[11.5px] font-bold tracking-[0.14em] text-mint">CONFIRMATION</div>
              <div className="mx-auto mt-4 grid h-16 w-16 place-items-center rounded-full bg-mint text-white shadow"><CheckCircle2 className="h-8 w-8" /></div>
              <h2 className="mt-5 text-[26px] md:text-[30px] font-extrabold text-navy">{d.confirmation.title}</h2>
              <p className="mt-3 text-[15px] leading-relaxed text-ink-700">{d.confirmation.body}</p>
              <div className="mt-6"><GradientButton to={d.confirmation.cta.to} data-testid="partnership-request-return">{d.confirmation.cta.label}</GradientButton></div>
            </div>
          </div>
        </section>
      </PageShell>
    );
  }

  return (
    <PageShell>
      {/* HERO */}
      <section className="px-6 pt-12 pb-8" data-testid="partnership-request-hero">
        <div className="mx-auto max-w-[1240px]">
          <div className="inline-flex items-center rounded-full bg-purple-50 px-3 py-1 text-[11px] font-bold tracking-widest text-purple">{d.hero.eyebrow}</div>
          <div className="mt-4 grid grid-cols-1 gap-8 lg:grid-cols-[1.15fr_1fr]">
            <div>
              <h1 className="text-[36px] md:text-[46px] font-extrabold leading-[1.05] tracking-tight text-navy">{d.hero.title}</h1>
              <p className="mt-5 text-[15.5px] leading-relaxed text-ink-500">{d.hero.desc}</p>
              <div className="mt-6"><LicenceFeeHighlight /></div>
            </div>
            <div className="rounded-2xl border border-canvas-200 bg-canvas-100 p-5 shadow-soft">
              <div className="text-[13px] font-bold text-navy">{d.hero.readinessTitle}</div>
              <ul className="mt-3 space-y-2">
                {d.hero.readiness.map((r, i) => (
                  <li key={i} className="flex items-start gap-2 text-[13px] text-ink-700"><Check className="mt-0.5 h-4 w-4 text-purple flex-none" /> {r}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FORM */}
      <section className="px-6 py-8">
        <div className="mx-auto max-w-[1240px] grid grid-cols-1 gap-8 lg:grid-cols-[1.4fr_1fr]">
          <form onSubmit={onSubmit} className="rounded-3xl border border-canvas-200 bg-white p-6 md:p-10 shadow-soft space-y-5" data-testid="partnership-request-form">
            <div>
              <h2 className="text-[20px] font-extrabold text-navy">{d.formTitle}</h2>
              <p className="mt-1 text-[12.5px] text-ink-500">{d.formHint}</p>
            </div>

            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <div>
                <label className={labelCls}>Organisation name *</label>
                <input required data-testid="pr-org" value={form.org} onChange={e => set('org', e.target.value)} className={`mt-2 ${inputCls}`} placeholder="Enter organisation name" />
              </div>
              <div>
                <label className={labelCls}>Website</label>
                <input type="url" data-testid="pr-website" value={form.website} onChange={e => set('website', e.target.value)} className={`mt-2 ${inputCls}`} placeholder="Optional" />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <div>
                <label className={labelCls}>Contact person *</label>
                <input required data-testid="pr-contact" value={form.contact} onChange={e => set('contact', e.target.value)} className={`mt-2 ${inputCls}`} placeholder="Enter full name" />
              </div>
              <div>
                <label className={labelCls}>Role or designation *</label>
                <input required data-testid="pr-role" value={form.role} onChange={e => set('role', e.target.value)} className={`mt-2 ${inputCls}`} placeholder="Enter designation" />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <div>
                <label className={labelCls}>Email *</label>
                <input type="email" required data-testid="pr-email" value={form.email} onChange={e => set('email', e.target.value)} className={`mt-2 ${inputCls}`} placeholder="name@organisation.org" />
              </div>
              <div>
                <label className={labelCls}>Phone *</label>
                <input required data-testid="pr-phone" value={form.phone} onChange={e => set('phone', e.target.value)} className={`mt-2 ${inputCls}`} placeholder="Enter phone number" />
              </div>
            </div>

            <div>
              <div className={labelCls}>Organisation type *</div>
              <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
                {d.orgTypes.map(t => (
                  <label key={t} className={`cursor-pointer rounded-xl border px-3 py-2.5 text-center text-[13px] font-semibold shadow-soft transition-colors ${form.orgType === t ? 'border-purple bg-purple-50 text-purple' : 'border-canvas-200 bg-white text-navy hover:border-purple/40'}`} data-testid={`pr-orgtype-${t.replace(/\s+/g,'-').toLowerCase()}`}>
                    <input type="radio" name="orgType" value={t} checked={form.orgType === t} onChange={e => set('orgType', e.target.value)} className="sr-only" required /> {t}
                  </label>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <div>
                <label className={labelCls}>Proposed geography *</label>
                <input required data-testid="pr-geography" value={form.geography} onChange={e => set('geography', e.target.value)} className={`mt-2 ${inputCls}`} placeholder="State, district, city or region" />
              </div>
              <div>
                <label className={labelCls}>Indicative number of institutions</label>
                <input data-testid="pr-institutions" value={form.institutions} onChange={e => set('institutions', e.target.value)} className={`mt-2 ${inputCls}`} placeholder="Enter approximate count" />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <div>
                <label className={labelCls}>Indicative learner reach</label>
                <input data-testid="pr-learners" value={form.learners} onChange={e => set('learners', e.target.value)} className={`mt-2 ${inputCls}`} placeholder="Enter approximate learner count" />
              </div>
              <div>
                <label className={labelCls}>Expected timeline</label>
                <input data-testid="pr-timeline" value={form.timeline} onChange={e => set('timeline', e.target.value)} className={`mt-2 ${inputCls}`} placeholder="Proposed start period or programme duration" />
              </div>
            </div>

            <div>
              <div className={labelCls}>Partnership interest *</div>
              <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                {d.interests.map(u => {
                  const on = form.interests.includes(u);
                  return (
                    <label key={u} className={`cursor-pointer rounded-xl border px-4 py-2.5 text-[13px] font-semibold shadow-soft transition-colors ${on ? 'border-purple bg-purple-50 text-purple' : 'border-canvas-200 bg-white text-navy hover:border-purple/40'}`} data-testid={`pr-interest-${u.replace(/\s+/g,'-').toLowerCase()}`}>
                      <input type="checkbox" checked={on} onChange={() => toggleArr('interests', u)} className="sr-only" /> {u}
                    </label>
                  );
                })}
              </div>
            </div>

            <div>
              <label className={labelCls}>Programme objective *</label>
              <textarea required rows={3} data-testid="pr-objective" value={form.objective} onChange={e => set('objective', e.target.value)} className={`mt-2 ${inputCls}`} placeholder="Describe the educational or social-impact objective." />
            </div>

            <div>
              <label className={labelCls}>Proposed contribution</label>
              <textarea rows={3} data-testid="pr-contribution" value={form.contribution} onChange={e => set('contribution', e.target.value)} className={`mt-2 ${inputCls}`} placeholder="Funding, programme ownership, implementation, research, technology or expertise." />
            </div>

            <div>
              <label className={labelCls}>Questions or constraints</label>
              <textarea rows={3} data-testid="pr-questions" value={form.questions} onChange={e => set('questions', e.target.value)} className={`mt-2 ${inputCls}`} placeholder="Approvals, geography, schedule, hardware, logistics or reporting requirements." />
            </div>

            <div>
              <div className={labelCls}>Agreements</div>
              <div className="mt-3 space-y-2">
                {d.agreements.map((r, i) => (
                  <label key={i} className={`flex items-start gap-3 rounded-xl border p-4 shadow-soft cursor-pointer transition-colors ${form.agreements[i] ? 'border-mint bg-mint-50' : 'border-canvas-200 bg-white hover:border-purple/40'}`} data-testid={`pr-agree-${i}`}>
                    <input type="checkbox" checked={form.agreements[i]} onChange={() => toggleAgree(i)} className="mt-1 h-4 w-4 rounded border-canvas-200 text-purple focus:ring-purple/40" />
                    <span className="text-[13.5px] text-ink-700">{r}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-4 pt-4 border-t border-canvas-200">
              <button type="submit" disabled={!canSubmit} data-testid="pr-submit" className={`inline-flex items-center gap-1 rounded-lg px-6 py-3 text-[14px] font-semibold text-white shadow-soft ${canSubmit ? 'bg-brand-gradient hover:brightness-110' : 'bg-canvas-200 text-ink-500 cursor-not-allowed'}`}>{submitting ? 'Submitting…' : d.submit} <ArrowRight className="h-4 w-4" /></button>
              <div className="text-[12px] text-ink-500">
                <div>{d.submitNote}</div>
                <div className="mt-0.5 italic">{d.privacy}</div>
              </div>
            </div>
            {submitError && (
              <div className="rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-[12.5px] text-red-700" data-testid="pr-submit-error">{submitError}</div>
            )}
          </form>

          {/* SIDE PANEL */}
          <div className="space-y-6">
            <div className="rounded-3xl border border-canvas-200 bg-white p-6 shadow-soft" data-testid="partnership-request-next">
              <h3 className="text-[18px] font-extrabold text-navy">{d.nextTitle}</h3>
              <ol className="mt-4 space-y-3">
                {d.next.map((n, i) => (
                  <li key={i} className="flex gap-3">
                    <span className="grid h-8 w-8 flex-none place-items-center rounded-full bg-purple text-white text-[13px] font-bold">{i + 1}</span>
                    <div>
                      <div className="text-[13.5px] font-bold text-navy">{n.title}</div>
                      <div className="text-[12px] text-ink-500">{n.desc}</div>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
            <div className="rounded-3xl border border-canvas-200 bg-white p-6 shadow-soft" data-testid="partnership-request-readiness">
              <h3 className="text-[18px] font-extrabold text-navy">{d.readinessTitle}</h3>
              <div className="mt-4 grid grid-cols-2 gap-3">
                {d.readinessItems.map((b, i) => (
                  <div key={i} className="rounded-xl bg-canvas-100 p-3 border border-canvas-200">
                    <LetterBadge letter={b.letter} color={b.color} size="sm" />
                    <div className="mt-2 text-[13px] font-bold text-navy">{b.title}</div>
                    <div className="text-[11.5px] text-ink-500">{b.desc}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-24" />
    </PageShell>
  );
}
